<script lang="ts">
export default {
  metaInfo: () => ({
    title: "Transaction History | TinyURL: The Original Link Shortener",
  }),
};
</script>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { isNullish, TuPaginator } from "@tinyurldev/tu-ui-vue";

import BillingApi from "@Modules/billing/Api/BillingApi";
import type { Invoice } from "@Modules/billing/Data/Models/InvoiceModel";
import BillingTransactionsView from "@Modules/billing/Views/BillingTransactionsView.vue";
import BillingViewModel, { PER_PAGE } from "@Modules/billing/ViewModels/BillingViewModel";
import type { SubscriptionData } from "@Modules/subscriptions/Data/Models/SubscriptionModel";
import SettingsTopBar from "@Modules/teams/components/SettingsTopBar.vue";
import { useCurrentOrganization } from "@Modules/teams/composables/useCurrentOrganization";
import OrgSettingsLayout from "@Modules/teams/Views/OrgSettingsLayout.vue";
import { XAuthStore } from "@Modules/users/Store/XAuthStore";
import usePaddle from "@Composables/usePaddle";
import useTranslation from "@Composables/useTranslation";
import SettingsContentLayout from "@Layouts/settings/LayoutContent.vue";

//
// Setup
const { currentOrganizationSlug, currentOrganization, currentWorkspace } = useCurrentOrganization();
const { openPaddleCompactCheckoutByTransaction } = usePaddle();
const { __ } = useTranslation();
const auth = XAuthStore();
const billingApi = BillingApi();

//
// Data
/** The transaction history has no subscription table, so the view model gets none */
const subscriptions = ref<SubscriptionData[]>([]);

const vm = BillingViewModel({
  orgSlug: currentOrganizationSlug,
  subscriptions,
  billingApi,
  openCompactCheckout: openPaddleCompactCheckoutByTransaction,
});

//
// Computed
const isOrgContext = computed<boolean>(() => !isNullish(currentOrganizationSlug.value));

const canManageSubscriptions = computed<boolean>(() => {
  return currentOrganization.value?.can("manage_subscriptions") ?? false;
});

/**
 * Support can hide a user's invoices from them, and then the history is read for nobody.
 * Only the single user carries the flag; no organization equivalent exists.
 */
const areInvoicesHidden = computed<boolean>(() => !isOrgContext.value && auth.userModel.hasHiddenInvoices());

/** The rows the table shows, which is nothing at all while the invoices are hidden */
const invoiceRows = computed<Invoice[]>(() => (areInvoicesHidden.value ? [] : vm.pagedInvoices.value));

/** Hidden invoices are never loaded, so the table is done waiting from the first paint */
const isTableReady = computed<boolean>(() => areInvoicesHidden.value || vm.invoicesReady.value);

const isPaginatorVisible = computed<boolean>(() => {
  return !areInvoicesHidden.value && vm.invoicesReady.value && vm.pageCount.value > 1;
});

//
// Methods
function onPageChange(page: number): void {
  vm.setPage(page);
}

//
// Lifecycle
onMounted(() => {
  if (isOrgContext.value && !canManageSubscriptions.value) {
    return;
  }

  if (areInvoicesHidden.value) {
    return;
  }

  vm.loadInvoices();
});

//
// Exposed Test Vars
defineExpose({
  isOrgContext,
  canManageSubscriptions,
  areInvoicesHidden,
  invoiceRows,
  isTableReady,
  isPaginatorVisible,
  onPageChange,
  vm,
});
</script>

<template>
  <OrgSettingsLayout
    v-if="isOrgContext"
    :organization="currentOrganization"
    :workspace="currentWorkspace"
    :can="canManageSubscriptions"
  >
    <SettingsTopBar :title="__('settings_billing_page.transaction.title')" />

    <div class="transactions-page">
      <BillingTransactionsView
        :invoices="invoiceRows"
        :is-ready="isTableReady"
        :max-rows="PER_PAGE"
        :download-invoice="vm.downloadInvoice"
        :pay-invoice="vm.payInvoice"
        :open-hosted-invoice="vm.openHostedInvoice"
        :paying-invoice-id="vm.payingInvoiceId.value"
        :hosted-invoice-id="vm.hostedInvoiceId.value"
      />

      <div
        v-if="isPaginatorVisible"
        class="transactions-page__paginator-wrapper"
      >
        <TuPaginator
          class="transactions-page__paginator _transactions_paginator"
          :current-page="vm.currentPage.value"
          :pages="vm.pageCount.value"
          @page-change="onPageChange"
        />
      </div>
    </div>
  </OrgSettingsLayout>

  <SettingsContentLayout
    v-else
    class="transactions-page"
  >
    <template #page-header>
      <RouterLink
        class="transactions-page__back-link _back_to_billing"
        :to="{ name: 'settings.billing' }"
      >
        <i
          class="transactions-page__back-icon fa-solid fa-arrow-left-long"
          aria-hidden="true"
        />
        {{ __("settings_billing_page.transaction.back_to_billing") }}
      </RouterLink>
    </template>

    <template #page-title>
      <span class="_transactions_page_title">{{ __("settings_billing_page.transaction.title") }}</span>
    </template>

    <BillingTransactionsView
      :invoices="invoiceRows"
      :is-ready="isTableReady"
      :max-rows="PER_PAGE"
      :download-invoice="vm.downloadInvoice"
      :pay-invoice="vm.payInvoice"
      :open-hosted-invoice="vm.openHostedInvoice"
      :paying-invoice-id="vm.payingInvoiceId.value"
      :hosted-invoice-id="vm.hostedInvoiceId.value"
    />

    <div
      v-if="isPaginatorVisible"
      class="transactions-page__paginator-wrapper"
    >
      <TuPaginator
        class="transactions-page__paginator _transactions_paginator"
        :current-page="vm.currentPage.value"
        :pages="vm.pageCount.value"
        @page-change="onPageChange"
      />
    </div>
  </SettingsContentLayout>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.transactions-page {
  &__back-link {
    display: inline-flex;
    align-items: baseline;
    gap: spacing.$xxxs;
    font-size: typography.$s-font-size;
    font-weight: typography.$font-weight-semi-bold;
  }

  &__back-icon {
    font-size: typography.$s-font-size;
  }

  &__paginator-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: spacing.$s;
  }
}
</style>
